// Mock data for TradingJournal
import {
  User,
  Portfolio,
  PositionsResponse,
  JournalData,
  PositionDetail,
  BrokerInfo,
  Broker,
  CategoryTimeframe,
  TimelineEventType,
  TradeOutcome,
  PositionStatus,
  TransactionType,
  StoplossTrigger,
} from '@shared/index';

export const mockUser: User = {
  id: 'user-1',
  name: 'Rahul',
  email: 'rahul@example.com',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Rahul',
};

export const mockPortfolios: Portfolio[] = [
  {
    id: '1',
    userId: 'user-1',
    name: 'Growth Portfolio',
    totalSize: 1450000,
    currentValue: 1668400,
    totalPnL: 218400,
    totalPnLPercentage: 15.06,
    lastUpdated: '3h ago',
    categories: [
      { name: 'Core', percentage: 60, timeframe: CategoryTimeframe.MONTHLY, color: '#4169E1', description: 'High-conviction long-term positions with strong fundamentals.' },
      { name: 'Satellite', percentage: 30, timeframe: CategoryTimeframe.WEEKLY, color: '#10B981', description: 'Swing trade positions following short-term momentum.' },
      { name: 'Tactical', percentage: 10, timeframe: CategoryTimeframe.DAILY, color: '#F59E0B', description: 'Short-term opportunistic trades based on news and events.' }
    ],
    brokers: [Broker.ZERODHA, Broker.GROWW]
  },
  {
    id: '2',
    userId: 'user-1',
    name: 'Conservative Portfolio',
    totalSize: 875000,
    currentValue: 1000800,
    totalPnL: 125800,
    totalPnLPercentage: 14.37,
    lastUpdated: '1d ago',
    categories: [
      { name: 'Bonds', percentage: 70, timeframe: CategoryTimeframe.QUARTERLY, color: '#4169E1', description: 'Fixed income investments for stability.' },
      { name: 'Equity', percentage: 30, timeframe: CategoryTimeframe.MONTHLY, color: '#8B5CF6', description: 'Diversified equity positions for growth.' }
    ],
    brokers: [Broker.UPSTOX]
  },
  {
    id: '3',
    userId: 'user-1',
    name: 'Tech Focus Portfolio',
    totalSize: 520000,
    currentValue: 611000,
    totalPnL: 91000,
    totalPnLPercentage: 17.5,
    lastUpdated: '2h ago',
    categories: [
      { name: 'IT Services', percentage: 80, timeframe: CategoryTimeframe.WEEKLY, color: '#4169E1', description: 'Technology sector focused investments.' },
      { name: 'Fintech', percentage: 20, timeframe: CategoryTimeframe.BI_WEEKLY, color: '#EC4899', description: 'Financial technology companies.' }
    ],
    brokers: [Broker.ZERODHA, Broker.ANGELONE]
  }
];

export const mockCurrentPositions: Record<string, PositionsResponse> = {
  '1': {
    summary: {
      totalInvestment: 245680,
      currentValue: 278450,
      unrealizedPnL: 32770,
      totalReturn: 13.34,
      residualRisk: 22530,
      dailyChange: 4860,
      dailyChangePercent: 1.78
    },
    positions: [
      {
        id: 's1',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE467B01029',
        ticker: 'TCS',
        name: 'Tata Consultancy Services',
        exchange: { name: 'NSE' as const },
        category: 'Core',
        broker: Broker.ZERODHA,
        status: PositionStatus.OPEN,
        totalQuantity: 15,
        availableQuantity: 12.5,
        avgBuyPrice: 3450,
        currentPrice: 4188,
        investedAmount: 43125,
        currentValue: 52350,
        unrealizedPnL: 9225,
        unrealizedPnLPercent: 21.39,
        realizedPnL: 1500,
        stoploss: 3240,
        residualRisk: 4500,
        trancheCount: 3,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 2.7, quantity: 5, price: 3400 },
          { id: 't2', type: TransactionType.BUY, percentage: 2.5, quantity: 5, price: 3500 },
          { id: 't3', type: TransactionType.BUY, percentage: 2.4, quantity: 5, price: 3450 },
          { id: 't4', type: TransactionType.SELL, percentage: -1.5, quantity: 2.5, price: 4000 }
        ],
        dailyChange: 2.1,
        weeklyChange: 4.3,
        durationDays: 95
      },
      {
        id: 's2',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE002A01018',
        ticker: 'RELIANCE',
        name: 'Reliance Industries',
        exchange: { name: 'NSE' as const },
        category: 'Satellite',
        broker: Broker.GROWW,
        status: PositionStatus.OPEN,
        totalQuantity: 15,
        availableQuantity: 15,
        avgBuyPrice: 2320,
        currentPrice: 2200,
        investedAmount: 34800,
        currentValue: 33000,
        unrealizedPnL: -1800,
        unrealizedPnLPercent: -5.17,
        realizedPnL: 0,
        stoploss: 2100,
        residualRisk: 2300,
        trancheCount: 2,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 1.8, quantity: 8, price: 2350 },
          { id: 't2', type: TransactionType.BUY, percentage: 1.6, quantity: 7, price: 2280 }
        ],
        dailyChange: -1.8,
        weeklyChange: -3.1,
        durationDays: 42
      },
      {
        id: 's3',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE040A01034',
        ticker: 'HDFC',
        name: 'HDFC Bank Limited',
        exchange: { name: 'NSE' as const },
        category: 'Core',
        broker: Broker.UPSTOX,
        status: PositionStatus.OPEN,
        totalQuantity: 30,
        availableQuantity: 25,
        avgBuyPrice: 1580,
        currentPrice: 1720,
        investedAmount: 39500,
        currentValue: 43000,
        unrealizedPnL: 3500,
        unrealizedPnLPercent: 8.86,
        realizedPnL: 2000,
        stoploss: 1520,
        residualRisk: 1820,
        trancheCount: 4,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 1.1, quantity: 10, price: 1600 },
          { id: 't2', type: TransactionType.BUY, percentage: 0.9, quantity: 8, price: 1550 },
          { id: 't3', type: TransactionType.BUY, percentage: 0.8, quantity: 7, price: 1585 },
          { id: 't4', type: TransactionType.BUY, percentage: 0.6, quantity: 5, price: 1590 },
          { id: 't5', type: TransactionType.SELL, percentage: -0.5, quantity: 5, price: 1700 }
        ],
        dailyChange: 1.2,
        weeklyChange: 2.8,
        durationDays: 128
      },
      {
        id: 's4',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE009A01021',
        ticker: 'INFY',
        name: 'Infosys Limited',
        exchange: { name: 'NSE' as const },
        category: 'Satellite',
        broker: Broker.ZERODHA,
        status: PositionStatus.OPEN,
        totalQuantity: 20,
        availableQuantity: 20,
        avgBuyPrice: 1420,
        currentPrice: 1680,
        investedAmount: 28400,
        currentValue: 33600,
        unrealizedPnL: 5200,
        unrealizedPnLPercent: 18.31,
        realizedPnL: 0,
        stoploss: 1380,
        residualRisk: 1400,
        trancheCount: 3,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 1.0, quantity: 8, price: 1400 },
          { id: 't2', type: TransactionType.BUY, percentage: 0.9, quantity: 6, price: 1420 },
          { id: 't3', type: TransactionType.BUY, percentage: 0.8, quantity: 6, price: 1450 }
        ],
        dailyChange: 3.2,
        weeklyChange: 5.4,
        durationDays: 68
      },
      {
        id: 's5',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE075A01022',
        ticker: 'WIPRO',
        name: 'Wipro Limited',
        exchange: { name: 'NSE' as const },
        category: 'Core',
        broker: Broker.GROWW,
        status: PositionStatus.OPEN,
        totalQuantity: 60,
        availableQuantity: 50,
        avgBuyPrice: 420,
        currentPrice: 520,
        investedAmount: 21000,
        currentValue: 26000,
        unrealizedPnL: 5000,
        unrealizedPnLPercent: 23.81,
        realizedPnL: 1200,
        stoploss: 400,
        residualRisk: 400,
        trancheCount: 4,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 0.9, quantity: 15, price: 400 },
          { id: 't2', type: TransactionType.BUY, percentage: 0.8, quantity: 15, price: 410 },
          { id: 't3', type: TransactionType.BUY, percentage: 0.7, quantity: 15, price: 430 },
          { id: 't4', type: TransactionType.BUY, percentage: 0.6, quantity: 15, price: 440 },
          { id: 't5', type: TransactionType.SELL, percentage: -0.4, quantity: 10, price: 500 }
        ],
        dailyChange: 2.8,
        weeklyChange: 6.2,
        durationDays: 152
      },
      {
        id: 's6',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE154A01025',
        ticker: 'ITC',
        name: 'ITC Limited',
        exchange: { name: 'NSE' as const },
        category: 'Tactical',
        broker: Broker.UPSTOX,
        status: PositionStatus.OPEN,
        totalQuantity: 40,
        availableQuantity: 40,
        avgBuyPrice: 380,
        currentPrice: 440,
        investedAmount: 15200,
        currentValue: 17600,
        unrealizedPnL: 2400,
        unrealizedPnLPercent: 15.79,
        realizedPnL: 0,
        stoploss: 360,
        residualRisk: 300,
        trancheCount: 4,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 0.5, quantity: 10, price: 370 },
          { id: 't2', type: TransactionType.BUY, percentage: 0.5, quantity: 10, price: 375 },
          { id: 't3', type: TransactionType.BUY, percentage: 0.4, quantity: 10, price: 385 },
          { id: 't4', type: TransactionType.BUY, percentage: 0.4, quantity: 10, price: 390 }
        ],
        dailyChange: 1.5,
        weeklyChange: 3.8,
        durationDays: 86
      }
    ]
  }
};

export const mockJournalData: Record<string, JournalData> = {
  '1': {
    summary: {
      totalPositions: 47,
      closedPositions: 34,
      winningPositions: 28,
      losingPositions: 6,
      winRate: 82.35,
      avgWinAmount: 8450,
      avgLossAmount: 3200,
      largestWin: 24500,
      largestLoss: 8200,
      p90Win: 18000,
      p90Loss: 6500,
      totalPnL: 218400,
      avgROI: 24.8,
      avgHoldingPeriod: 32
    },
    positions: [
      {
        id: 'ht1',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE009A01021',
        ticker: 'INFY',
        name: 'Infosys Limited',
        exchange: { name: 'NSE' as const },
        category: 'Core',
        broker: Broker.ZERODHA,
        status: PositionStatus.CLOSED,
        totalQuantity: 50,
        availableQuantity: 0,
        avgBuyPrice: 1420,
        currentPrice: 1680,
        investedAmount: 71000,
        currentValue: 0,
        unrealizedPnL: 0,
        unrealizedPnLPercent: 0,
        realizedPnL: 12400,
        stoploss: 1380,
        residualRisk: 0,
        trancheCount: 3,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 1.6, quantity: 20, price: 1400 },
          { id: 't2', type: TransactionType.BUY, percentage: 1.5, quantity: 15, price: 1420 },
          { id: 't3', type: TransactionType.BUY, percentage: 1.4, quantity: 15, price: 1450 },
          { id: 't4', type: TransactionType.SELL, percentage: -4.8, quantity: 50, price: 1680 }
        ],
        durationDays: 45,
        closedDate: '2024-11-15',
        exitPrice: 1680,
        roi: 18.3,
        outcome: TradeOutcome.WIN
      },
      {
        id: 'ht2',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE040A01034',
        ticker: 'HDFC',
        name: 'HDFC Bank Limited',
        exchange: { name: 'NSE' as const },
        category: 'Satellite',
        broker: Broker.UPSTOX,
        status: PositionStatus.CLOSED,
        totalQuantity: 30,
        availableQuantity: 0,
        avgBuyPrice: 1580,
        currentPrice: 1720,
        investedAmount: 47400,
        currentValue: 0,
        unrealizedPnL: 0,
        unrealizedPnLPercent: 0,
        realizedPnL: 8400,
        stoploss: 1540,
        residualRisk: 0,
        trancheCount: 2,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 1.8, quantity: 20, price: 1550 },
          { id: 't2', type: TransactionType.BUY, percentage: 1.4, quantity: 10, price: 1640 },
          { id: 't3', type: TransactionType.SELL, percentage: -3.5, quantity: 30, price: 1720 }
        ],
        durationDays: 62,
        closedDate: '2024-11-10',
        exitPrice: 1720,
        roi: 8.9,
        outcome: TradeOutcome.WIN
      },
      {
        id: 'ht3',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE002A01018',
        ticker: 'RELI',
        name: 'Reliance Industries',
        exchange: { name: 'NSE' as const },
        category: 'Speculative',
        broker: Broker.GROWW,
        status: PositionStatus.CLOSED,
        totalQuantity: 20,
        availableQuantity: 0,
        avgBuyPrice: 2450,
        currentPrice: 2200,
        investedAmount: 49000,
        currentValue: 0,
        unrealizedPnL: 0,
        unrealizedPnLPercent: 0,
        realizedPnL: -5000,
        stoploss: 2300,
        residualRisk: 0,
        trancheCount: 1,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 3.4, quantity: 20, price: 2450 },
          { id: 't2', type: TransactionType.SELL, percentage: -3.0, quantity: 20, price: 2200 }
        ],
        durationDays: 28,
        closedDate: '2024-11-05',
        exitPrice: 2200,
        roi: -10.2,
        outcome: TradeOutcome.LOSS
      },
      {
        id: 'ht4',
        portfolioId: '1',
        userId: 'user-1',
        isin: 'INE075A01022',
        ticker: 'WIP',
        name: 'Wipro Limited',
        exchange: { name: 'NSE' as const },
        category: 'Core',
        broker: Broker.GROWW,
        status: PositionStatus.CLOSED,
        totalQuantity: 100,
        availableQuantity: 0,
        avgBuyPrice: 420,
        currentPrice: 520,
        investedAmount: 42000,
        currentValue: 0,
        unrealizedPnL: 0,
        unrealizedPnLPercent: 0,
        realizedPnL: 15600,
        stoploss: 430,
        residualRisk: 0,
        trancheCount: 4,
        trancheBreakdown: [
          { id: 't1', type: TransactionType.BUY, percentage: 1.2, quantity: 30, price: 400 },
          { id: 't2', type: TransactionType.BUY, percentage: 1.1, quantity: 25, price: 410 },
          { id: 't3', type: TransactionType.BUY, percentage: 1.0, quantity: 25, price: 430 },
          { id: 't4', type: TransactionType.BUY, percentage: 0.9, quantity: 20, price: 450 },
          { id: 't5', type: TransactionType.SELL, percentage: -3.6, quantity: 100, price: 520 }
        ],
        durationDays: 89,
        closedDate: '2024-10-28',
        exitPrice: 520,
        roi: 23.8,
        outcome: TradeOutcome.WIN
      }
    ]
  }
};

export const mockStockDetails: Record<string, PositionDetail> = {
  's1': {
    // Position base fields
    id: 's1',
    portfolioId: '1',
    userId: 'user-1',
    isin: 'INE467B01029',
    ticker: 'TCS',
    name: 'Tata Consultancy Services',
    exchange: { name: 'NSE' as const },
    category: 'Core',
    broker: Broker.ZERODHA,
    status: PositionStatus.OPEN,
    
    // Computed metrics (flat, not nested)
    totalQuantity: 12.5,
    availableQuantity: 9.5,
    avgBuyPrice: 3600,
    currentPrice: 4188,
    investedAmount: 43000,
    currentValue: 52350,
    unrealizedPnL: 7350,
    unrealizedPnLPercent: 16.33,
    realizedPnL: 2850,
    stoploss: 3240,
    residualRisk: 4500,
    trancheCount: 3,
    dailyChange: 2.1,
    weeklyChange: 4.3,
    durationDays: 95,
    
    // PositionDetail extensions
    tranches: [
      {
        id: 't1',
        orderId: 'ORD-TCS-001',
        stockId: 's1',
        type: TransactionType.BUY,
        timestamp: '2024-03-10T10:30:00.000Z',
        quantity: 3.5,
        price: 3800
      },
      {
        id: 't2',
        orderId: 'ORD-TCS-002',
        stockId: 's1',
        type: TransactionType.BUY,
        timestamp: '2024-02-20T14:15:00.000Z',
        quantity: 4,
        price: 3650
      },
      {
        id: 't2-sell',
        orderId: 'ORD-TCS-003',
        stockId: 's1',
        type: TransactionType.SELL,
        timestamp: '2024-03-05T11:20:00.000Z',
        quantity: 2,
        price: 4075
      },
      {
        id: 't3',
        orderId: 'ORD-TCS-004',
        stockId: 's1',
        type: TransactionType.BUY,
        timestamp: '2024-01-15T09:45:00.000Z',
        quantity: 5,
        price: 3400
      },
      {
        id: 't3-sell',
        orderId: 'ORD-TCS-005',
        stockId: 's1',
        type: TransactionType.SELL,
        timestamp: '2024-02-28T15:30:00.000Z',
        quantity: 1,
        price: 3900
      }
    ],
    stoplossHistory: [
      {
        id: 'sl1',
        timestamp: '2024-01-15T09:45:00.000Z',
        newStoploss: 3000,
        trigger: StoplossTrigger.BUY,
        trancheId: 't3'
      },
      {
        id: 'sl2',
        timestamp: '2024-02-20T14:15:00.000Z',
        newStoploss: 3200,
        trigger: StoplossTrigger.BUY,
        trancheId: 't2'
      },
      {
        id: 'sl3',
        timestamp: '2024-03-10T10:30:00.000Z',
        newStoploss: 3240,
        trigger: StoplossTrigger.BUY,
        trancheId: 't1'
      }
    ],
    notes: '',
    timeline: [
      {
        id: 'n1',
        date: '2024-03-10',
        time: '10:30 AM',
        type: TimelineEventType.BUY,
        tranche: 't1',
        quantity: 3.5,
        price: 3800,
        content: 'Entry based on strong Q4 earnings report and positive sector outlook.',
        autoGenerated: true,
        editable: false
      },
      {
        id: 'n2',
        date: '2024-02-20',
        time: '02:15 PM',
        type: TimelineEventType.BUY,
        tranche: 't2',
        quantity: 4,
        price: 3650,
        content: 'Dollar-cost averaging into position as IT sector shows recovery signs.',
        autoGenerated: true,
        editable: false
      },
      {
        id: 'n3',
        date: '2024-02-25',
        time: '11:45 AM',
        type: TimelineEventType.SELL,
        tranche: 't2',
        quantity: 2,
        price: 4075,
        content: 'Booking profits as stock hit resistance at ₹4,100 level.',
        autoGenerated: true,
        editable: false
      },
      {
        id: 'n4',
        date: '2024-01-15',
        time: '09:00 AM',
        type: TimelineEventType.BUY,
        tranche: 't3',
        quantity: 5,
        price: 3400,
        content: 'Good entry point based on technical analysis.',
        autoGenerated: true,
        editable: false
      },
      {
        id: 'n5',
        date: '2024-04-05',
        time: '03:20 PM',
        type: TimelineEventType.NOTE,
        content: 'Company announced new client wins in North America. Maintaining position with trailing stop at ₹3,240.',
        autoGenerated: false,
        editable: true
      }
    ]
  },
  's3': {
    // Position base fields
    id: 's3',
    portfolioId: '1',
    userId: 'user-1',
    isin: 'INE040A01034',
    ticker: 'HDFC',
    name: 'HDFC Bank Limited',
    exchange: { name: 'NSE' as const },
    category: 'Core',
    broker: Broker.UPSTOX,
    status: PositionStatus.OPEN,
    
    // Computed metrics (flat, not nested)
    totalQuantity: 25,
    availableQuantity: 25,
    avgBuyPrice: 1580,
    currentPrice: 1720,
    investedAmount: 39500,
    currentValue: 43000,
    unrealizedPnL: 3500,
    unrealizedPnLPercent: 8.86,
    realizedPnL: 0,
    stoploss: 1520,
    residualRisk: 1820,
    trancheCount: 3,
    dailyChange: 1.2,
    weeklyChange: 2.8,
    durationDays: 128,
    
    // PositionDetail extensions
    tranches: [
      {
        id: 't1',
        orderId: 'ORD-HDFC-001',
        stockId: 's3',
        type: TransactionType.BUY,
        timestamp: '2024-04-05T10:00:00.000Z',
        quantity: 10,
        price: 1600
      },
      {
        id: 't2',
        orderId: 'ORD-HDFC-002',
        stockId: 's3',
        type: TransactionType.BUY,
        timestamp: '2024-03-15T14:30:00.000Z',
        quantity: 8,
        price: 1550
      },
      {
        id: 't3',
        orderId: 'ORD-HDFC-003',
        stockId: 's3',
        type: TransactionType.BUY,
        timestamp: '2024-02-28T11:15:00.000Z',
        quantity: 7,
        price: 1585
      }
    ],
    stoplossHistory: [
      {
        id: 'sl1',
        timestamp: '2024-02-28T11:15:00.000Z',
        newStoploss: 1500,
        trigger: StoplossTrigger.BUY,
        trancheId: 't3'
      },
      {
        id: 'sl2',
        timestamp: '2024-03-15T14:30:00.000Z',
        newStoploss: 1510,
        trigger: StoplossTrigger.BUY,
        trancheId: 't2'
      },
      {
        id: 'sl3',
        timestamp: '2024-04-05T10:00:00.000Z',
        newStoploss: 1520,
        trigger: StoplossTrigger.BUY,
        trancheId: 't1'
      }
    ],
    notes: '',
    timeline: []
  }
};

export const mockBrokers: BrokerInfo[] = [
  { id: Broker.GROWW, name: 'Groww', logo: 'G' },
  { id: Broker.ZERODHA, name: 'Zerodha', logo: 'Z' },
  { id: Broker.UPSTOX, name: 'Upstox', logo: 'U' },
  { id: Broker.ANGELONE, name: 'Angel One', logo: 'A' },
  { id: Broker.ICICI_DIRECT, name: 'ICICI Direct', logo: 'I' },
  { id: Broker.HDFC_SECURITIES, name: 'HDFC Securities', logo: 'H' },
  { id: Broker.KOTAK, name: 'Kotak Securities', logo: 'K' }
];

// Helper to get broker display name
export const getBrokerDisplayName = (broker: Broker): string => {
  const brokerInfo = mockBrokers.find(b => b.id === broker);
  return brokerInfo?.name || broker;
};

// Helper to get broker logo
export const getBrokerLogo = (broker: Broker): string => {
  const brokerInfo = mockBrokers.find(b => b.id === broker);
  return brokerInfo?.logo || broker.charAt(0).toUpperCase();
};
